/**
 * What the Packages and Orders lists ask the vendor for (MYP-126).
 *
 * Each list keeps only the filters the vendor can apply itself — anything else would be applied
 * to one page and silently miss the rest. See `list-paging.ts` for what was checked live.
 */
import type { OrderStatus, OrderStatusFilter } from './orders.js';
import { ORDER_STAGES } from './orders.js';
import { orderStatusCodesFor } from './orders-wire.js';
import type { PackageStatus, PackageTab } from './packages.js';
import { PACKAGE_TABS } from './packages.js';
import { packageStatusCodesFor } from './packages-wire.js';

/* -------------------------------------------------------------------------------------------- *
 * Packages
 * -------------------------------------------------------------------------------------------- */

/** The Packages list's filters, all of which the vendor applies. */
export interface PackageListQuery {
  readonly tab: PackageTab;
  /** A full tracking number or order number. The vendor matches both exactly. */
  readonly search: string;
  /** A code from the vendor's `goods_type` dictionary, or `'all'`. */
  readonly goodsType: string;
  /**
   * Only packages that are not on any order yet — the vendor's `validStatus: 1`, checked live on
   * 8 October (40 of 309 packages, exactly the ones with no order number).
   */
  readonly notOrdered: boolean;
}

export const EMPTY_PACKAGE_LIST_QUERY: PackageListQuery = {
  tab: 'all',
  search: '',
  goodsType: 'all',
  notOrdered: false,
};

/** The groups each tab covers — the same split the tabs have always used. */
const TAB_GROUPS: Record<Exclude<PackageTab, 'all'>, readonly PackageStatus[]> = {
  on_the_way: ['on_the_way', 'arrived'],
  in_warehouse: ['in_warehouse'],
  in_order: ['in_order', 'shipped'],
};

/** The vendor status codes for a tab, or undefined for `all` (no status filter). */
export function packageTabStatusCodes(tab: PackageTab): readonly string[] | undefined {
  return tab === 'all' ? undefined : packageStatusCodesFor(TAB_GROUPS[tab]);
}

/** Reads the query back out of a URL; anything unknown falls back to "all". */
export function parsePackageListQuery(
  params: URLSearchParams,
  knownGoodsTypes: readonly string[],
): PackageListQuery {
  const tab = params.get('tab') ?? '';
  const goodsType = params.get('goodsType') ?? '';
  return {
    tab: PACKAGE_TABS.includes(tab as PackageTab) ? (tab as PackageTab) : 'all',
    search: params.get('q') ?? '',
    goodsType: knownGoodsTypes.includes(goodsType) ? goodsType : 'all',
    notOrdered: params.get('free') === '1',
  };
}

/** Whether anything narrows the list — drives Reset and the "no matches" message. */
export function hasActivePackageListQuery(query: PackageListQuery): boolean {
  return (
    query.tab !== 'all' ||
    query.search.trim() !== '' ||
    query.goodsType !== 'all' ||
    query.notOrdered
  );
}

/** A search box value as the vendor needs it: no surrounding space, no inner spaces. */
export function exactSearchTerm(value: string): string {
  return value.replace(/\s+/g, '');
}

/** `YYMMDD-nnnnnn`, with or without the dash, normalised to the vendor's form — or undefined. */
export function asOrderNumber(value: string): string | undefined {
  const match = /^(\d{6})-?(\d{6})$/.exec(exactSearchTerm(value));
  return match ? `${match[1]}-${match[2]}` : undefined;
}

/**
 * `package_list` parameters for one page.
 *
 * The search box takes a full tracking number (`inorderno`) or a full order number
 * (`outorderno`, MYP-127), which lists the packages on that order. Both are exact — checked live
 * on 8 October; part of a number finds nothing — and a number that matches nothing answers with
 * no rows, so nothing has to be double-checked here (unlike `order_list`, see `orderSearchField`).
 *
 * `tab` is passed separately so the same search and goods type can be asked once per tab for the
 * tab counts (with `limit: 1`, only `count` is read).
 */
export function packageListParams(
  query: PackageListQuery,
  page: number,
  size: number,
  tab: PackageTab = query.tab,
): Record<string, string> {
  const params: Record<string, string> = { page: String(page), limit: String(size) };
  const codes = packageTabStatusCodes(tab);
  if (codes) params['status'] = codes.join(',');
  const search = exactSearchTerm(query.search);
  if (search) {
    const orderNumber = asOrderNumber(search);
    if (orderNumber) params['outorderno'] = orderNumber;
    else params['inorderno'] = search;
  }
  if (query.goodsType !== 'all') params['goodstype'] = query.goodsType;
  if (query.notOrdered) params['validStatus'] = '1';
  return params;
}

/* -------------------------------------------------------------------------------------------- *
 * Create order — the packages a new order can use (MYP-130)
 * -------------------------------------------------------------------------------------------- */

/**
 * `package_list` parameters for one page of the packages a new order can use: the vendor's
 * orderable statuses (`package_status_for_order_list`) and `validStatus: 1`, "not on an order
 * yet". Together they are exactly the rule `eligibleOrderablePackages` applies on screen —
 * checked live on 9 October: 30 packages, paged with `page`/`limit`.
 *
 * The search is a full tracking number (`inorderno`, exact). The vendor has no partial or
 * item-name search and no sort, so neither is offered.
 */
export function orderablePackageParams(
  statusCodes: readonly string[],
  search: string,
  page: number,
  size: number,
): Record<string, string> {
  const params: Record<string, string> = {
    page: String(page),
    limit: String(size),
    status: statusCodes.join(','),
    validStatus: '1',
  };
  const term = exactSearchTerm(search);
  if (term) params['inorderno'] = term;
  return params;
}

/**
 * `package_list` parameters that answer which of these package ids can still go on a new order
 * — for the packages carried over from the Packages page. `pkgId` takes a list; with
 * `validStatus: 1` and the orderable statuses, one already on an order is left out (checked live
 * on 9 October).
 */
export function orderablePackagesByIdParams(
  statusCodes: readonly string[],
  ids: readonly string[],
): Record<string, string> {
  return {
    limit: '-1',
    pkgId: ids.join(','),
    status: statusCodes.join(','),
    validStatus: '1',
  };
}

/**
 * The total to page by. `package_list`'s `count` is one too high (310 for 309, 31 for 30), so:
 * - a short page (the last one) gives the true total;
 * - an empty page past the first means the list ends before it, so the total is at most the rows
 *   on the pages before it — 30 packages at 10 a page are reported as 31, page 4 comes back empty,
 *   and the total becomes 30 (not 31, which would leave an empty page 4 the pager cannot leave).
 *
 * A full page keeps the vendor's count: subtracting 1 there would hide the last package for good
 * if the vendor ever fixes its count. `useOrderablePackagePage` remembers the cap an empty page
 * gives, so going back to a full page does not bring the empty one back.
 */
export function pageTotal(count: number, page: number, size: number, rowsOnPage: number): number {
  if (rowsOnPage === 0) return page === 1 ? 0 : Math.min(count, (page - 1) * size);
  if (rowsOnPage < size) return (page - 1) * size + rowsOnPage;
  return count;
}

/**
 * The page the vendor's `count` says is the last one. Asking for it once gives the exact total
 * (`pageTotal` on its rows): 31 for 30 packages at 10 a page says page 4; page 4 comes back empty,
 * so the total is 30 — known from the first page, not only once someone pages to the end.
 */
export function lastPageNumber(count: number, size: number): number {
  return count <= 0 || size <= 0 ? 0 : Math.ceil(count / size);
}

/* -------------------------------------------------------------------------------------------- *
 * Orders
 * -------------------------------------------------------------------------------------------- */

/** The Orders list's filters, all of which the vendor applies. */
export interface OrderListQuery {
  /**
   * An order number, a package tracking number or shipping number (whole, or its start), or the
   * receiver's full name — see `orderSearch` and `orderSearchField`.
   */
  readonly search: string;
  readonly status: OrderStatusFilter;
  /** A channel id, or `'all'`. */
  readonly channelId: string;
}

export const EMPTY_ORDER_LIST_QUERY: OrderListQuery = {
  search: '',
  status: 'all',
  channelId: 'all',
};

/** Every status filter the chips offer, in chip order. */
export const ORDER_STATUS_FILTERS: readonly OrderStatusFilter[] = [
  'all',
  ...ORDER_STAGES,
  'cancelled',
  'exception',
];

function isOrderStatus(value: string): value is OrderStatus {
  return (
    value === 'cancelled' ||
    value === 'exception' ||
    (ORDER_STAGES as readonly string[]).includes(value)
  );
}

/** Reads the query back out of a URL. A channel is any numeric id; an unknown one matches nothing. */
export function parseOrderListQuery(params: URLSearchParams): OrderListQuery {
  const status = params.get('status') ?? '';
  const channelId = params.get('channel') ?? '';
  return {
    search: params.get('q') ?? '',
    status: isOrderStatus(status) ? status : 'all',
    channelId: /^\d+$/.test(channelId) ? channelId : 'all',
  };
}

export function hasActiveOrderListQuery(query: OrderListQuery): boolean {
  return query.status !== 'all' || query.channelId !== 'all' || query.search.trim() !== '';
}

/**
 * What a search box value is, for the vendor (MYP-127).
 *
 * An order number (`YYMMDD-nnnnnn`, dash optional) is looked up with `orderno`, which answers with
 * that one order. Anything else is text: a package tracking number, a shipping number or the
 * receiver's full name — which one is worked out by asking (`orderSearchField`). `term` is the text
 * with spaces removed, for the numbers; `name` keeps its inner spaces, for the name.
 */
export type OrderSearch =
  | { readonly kind: 'none' }
  | { readonly kind: 'orderNumber'; readonly orderNumber: string }
  | { readonly kind: 'text'; readonly term: string; readonly name: string };

export function orderSearch(value: string): OrderSearch {
  const term = exactSearchTerm(value);
  if (term === '') return { kind: 'none' };
  const orderNumber = asOrderNumber(term);
  if (orderNumber) return { kind: 'orderNumber', orderNumber };
  return { kind: 'text', term, name: value.trim().replace(/\s+/g, ' ') };
}

/** Which `order_list` field a text search matched — see `orderSearchField`. */
export type OrderSearchField = 'tracking' | 'shipping' | 'name';

/** The vendor codes a status filter covers, or undefined for `all`. */
export function orderStatusCodes(status: OrderStatusFilter): readonly string[] | undefined {
  return status === 'all' ? undefined : orderStatusCodesFor(status);
}

/**
 * `order_list` parameters for one request: one status code at most (the vendor takes one), the
 * channel, and — for a text search — the field it matched. An order-number search is a different
 * request (see `orderSearch`); a text search with no matched field sends no search at all, and the
 * caller shows "no matches" instead of asking.
 */
export function orderListParams(
  query: OrderListQuery,
  statusCode: string | undefined,
  page: number,
  limit: number,
  field?: OrderSearchField,
): Record<string, string> {
  const params: Record<string, string> = { page: String(page), limit: String(limit) };
  if (statusCode) params['qryStatus'] = statusCode;
  if (query.channelId !== 'all') params['qrychannel'] = query.channelId;
  const search = orderSearch(query.search);
  if (search.kind === 'text' && field) {
    if (field === 'tracking') params['inorderno'] = search.term;
    else if (field === 'shipping') params['outorderno'] = search.term;
    else params['qryRcvName'] = search.name;
  }
  return params;
}

/** What asking each field for a text search answered — the input to `orderSearchField`. */
export interface OrderSearchProbe {
  /** The order count for the same channel with no search at all. */
  readonly baseCount: number;
  /** `count` for `inorderno` = the text. */
  readonly trackingCount: number;
  /** The shipping numbers (`airno`) of the rows `outorderno` = the text returned. */
  readonly shippingNumbers: readonly string[];
  /** `count` for `qryRcvName` = the text. */
  readonly nameCount: number;
}

/**
 * Which field a text search really matched, or undefined for none (MYP-127).
 *
 * Checked live on 8 October: `order_list` matches a tracking number (`inorderno`) or a shipping
 * number (`outorderno`) whole or by its start, and a receiver's full name (`qryRcvName`, any
 * capitals). But when `inorderno` or `outorderno` matches nothing, the vendor drops the filter and
 * answers with **every** order — so an answer is only trusted when it shows it really filtered:
 *
 * - tracking: the count differs from the unfiltered count. (With one order or none on the account
 *   the two cannot be told apart, so a count there is taken at its word.)
 * - shipping: every row's shipping number starts with the text.
 * - name: any match (a name that matches nothing answers with nothing).
 *
 * Tried in that order; the first that matched is used.
 */
export function orderSearchField(
  term: string,
  probe: OrderSearchProbe,
): OrderSearchField | undefined {
  const { baseCount, trackingCount, shippingNumbers, nameCount } = probe;
  if (trackingCount > 0 && (trackingCount !== baseCount || baseCount <= 1)) return 'tracking';
  const upper = term.toUpperCase();
  if (
    shippingNumbers.length > 0 &&
    shippingNumbers.every((number) => number.toUpperCase().startsWith(upper))
  ) {
    return 'shipping';
  }
  if (nameCount > 0) return 'name';
  return undefined;
}
